import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "webrtc-adapter";
import { api } from "../../../api/apiService";
import { toast } from "react-toastify";

const schema = yup.object().shape({
    employee_id: yup
        .number()
        .typeError("Employee ID must be a number")
        .required("Employee ID is required"),
    // .positive("Employee ID must be a positive number")
    // .min(1, "Employee ID must be greater than zero"),
    image: yup
        .string()
        .required("Image is required"),
});

function Attendance() {
    const [submissionStatus, setSubmissionStatus] = useState("");
    const [image, setImage] = useState(null);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [isIdVerified, setIsIdVerified] = useState(false);
    const [verifyUser, setVerifyUser] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue, watch,
    } = useForm({
        resolver: yupResolver(schema),
    });
    const employeeId = watch("employee_id");
    const onSubmit = async (data) => {
        try {
            if (!image) {
                setSubmissionStatus("Error: Image is required.");
                return;
            }
            if (!isIdVerified) {
                setSubmissionStatus("Error: Employee ID not verified.");
                return;
            }
            const formData = new FormData();
            formData.append("employee_id", data.employee_id);
            formData.append("employee_image", image);

            const resp = await api.post("submit-attendance", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            toast.success(resp.data.message)
            reset();
            setImage(null);
            setIsCameraActive(false);
        } catch (error) {
            toast.error(error.data.message)
        }
    };

    useEffect(() => {
        if (isCameraActive) {
            startCamera();
        } else {
            stopCamera();
        }
    }, [isCameraActive]);

    // Start the camera
    const startCamera = async () => {
        setIsCameraActive(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error("Error accessing camera:", err);
        }
    };

    // Stop the camera
    const stopCamera = () => {
        const stream = videoRef.current?.srcObject;
        const tracks = stream?.getTracks();
        tracks?.forEach(track => track.stop());
        videoRef.current = null;
        setIsCameraActive(false);
    };

    // Capture
    const capturePhoto = () => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to a Blob (image file)
        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], "attendance-image.png", { type: "image/png" });
                setImage(file);
                setValue("image", file);
            }
            stopCamera();
        }, "image/png");
    };


    const VerifyId = async () => {
        if (!employeeId) {
            toast.error("Please enter Employee ID");
            return;
        }
        try {
            const response = await api.get(`verify-employee/${employeeId}`);
            toast.success("Employee ID verified!");
            setIsIdVerified(true);
            setVerifyUser(response?.data?.data)
        } catch (error) {
            toast.error(error?.data?.message || "Error verifying Employee ID");
            setIsIdVerified(false);
        }
    };

    const Avatar = (name) => {
        const initials = name
            ? name.split(" ").map(word => word[0].toUpperCase()).join("")
            : "NA";
        return (
            <div
                style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {initials}
            </div>
        );
    };

    return (
        <div className="container">
            <div className="row justify-content-center">
                <div className="col-lg-6 col-md-8">
                    <div className="d-flex justify-content-center py-4">
                        <Link to="/" className="logo d-flex align-items-center w-auto">
                            <span className="d-none d-lg-block">Attendance</span>
                        </Link>
                    </div>
                    {verifyUser ? (
                        <div className="d-flex justify-content-between align-items-center w-100 mb-2 shadow-sm rounded py-1 px-2">
                            <strong className='me-2'>Verified User
                                <i className="verified-icon bi bi-check-circle-fill text-success ms-1"></i>
                            </strong>
                            <div className='d-flex align-items-center'>
                                {verifyUser.profile_picture ? (
                                    <img
                                        src={verifyUser.profile_picture}
                                        alt="user"
                                        height={40}
                                        style={{ borderRadius: "50%" }}
                                    />
                                ) : (
                                    Avatar(verifyUser.name)
                                )}
                                <span className="ms-2 text-capitalize">{verifyUser.name}</span>
                            </div>
                        </div>
                    ) : (
                        <></>
                    )}
                    <div className="card pt-3">
                        <div className="card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="mb-3">
                                    <div className="mb-2">
                                        {isCameraActive && (
                                            <div>
                                                <video ref={videoRef} autoPlay width="100%" height="auto" />
                                            </div>
                                        )}

                                        {image ? (
                                            <div className='d-flex justify-content-center'>
                                                <img src={URL.createObjectURL(image)} alt="user" width="300" height="200" />
                                            </div>
                                        ) : (
                                            <div className='d-flex justify-content-center'>
                                                <div className='d-flex justify-content-center align-items-center bg-light' style={{ width: "300px", border: "1px solid black", height: "200px" }}>No Image</div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex justify-content-center gap-2 mt-3">
                                        {isCameraActive ? (
                                            <>
                                                <Button
                                                    className="btn btn-primary"
                                                    onClick={capturePhoto}
                                                >
                                                    Capture
                                                </Button>
                                                <Button
                                                    className="btn btn-secondary"
                                                    onClick={stopCamera}
                                                >
                                                    Close
                                                </Button>
                                            </>
                                        ) : (
                                            <Button
                                                className="btn btn-primary"
                                                onClick={startCamera}
                                            >
                                                Take Photo
                                            </Button>
                                        )}
                                    </div>
                                    <canvas ref={canvasRef} style={{ display: "none" }}></canvas>

                                    {/* Display image error message */}
                                    {errors.image && (
                                        <div className="invalid-feedback d-block">
                                            {errors.image.message}
                                        </div>
                                    )}
                                </div>
                                <div className='row g-3 mb-3'>
                                    <div className='col-sm-12 col-md-6'>
                                        <input
                                            type="number"
                                            id="employee_id"
                                            placeholder="Employee ID"
                                            className={`form-control ${errors.employee_id ? "is-invalid" : ""}`}
                                            {...register("employee_id")}
                                        />
                                        {errors.employee_id && (
                                            <div className="invalid-feedback">
                                                {errors.employee_id.message}
                                            </div>
                                        )}

                                    </div>
                                    <div className='col-sm-12 col-md-4'>
                                        <button className='btn btn-primary' onClick={VerifyId} disabled={!employeeId}>Verify</button>
                                    </div>
                                </div>

                                <div className="text-center">
                                    <Button type="submit" className="btn btn-primary"
                                        disabled={!employeeId || !image || !isIdVerified}>
                                        Submit
                                    </Button>
                                </div>

                                {submissionStatus && (
                                    <div className="mt-3 alert alert-info">{submissionStatus}</div>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Attendance;
